import React, { useRef, useEffect } from 'react';

const AnimatedGrid: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId: number;
    const gridSize = 40;
    let speed = 0.5;

    const setup = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      speed = canvas.width > 768 ? 0.5 : 0.3; // Slower on smaller screens
    };

    class Particle {
      x: number;
      y: number;
      size: number;
      speedX: number;
      speedY: number;
      life: number;
      maxLife: number;

      constructor(x: number, y: number, speedX: number, speedY: number) {
        this.x = x;
        this.y = y;
        this.size = Math.random() * 2 + 1;
        this.speedX = speedX;
        this.speedY = speedY;
        this.maxLife = Math.random() * 300 + 200;
        this.life = this.maxLife;
      }

      update() {
        this.x += this.speedX;
        this.y += this.speedY;

        if (this.life > 0) {
          this.life--;
        }
      }

      draw() {
        if (!ctx) return;
        const opacity = (this.life / this.maxLife) * 0.7; // Subtle base opacity
        ctx.fillStyle = `rgba(54, 169, 247, ${opacity})`;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    const particles: Particle[] = [];

    const animate = () => {
      if (!ctx) return;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      ctx.strokeStyle = 'rgba(30, 50, 80, 0.5)';
      ctx.lineWidth = 0.5;
      for (let x = 0; x <= canvas.width; x += gridSize) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, canvas.height);
        ctx.stroke();
      }
      for (let y = 0; y <= canvas.height; y += gridSize) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(canvas.width, y);
        ctx.stroke();
      }

      if (particles.length < 100) { // Slightly fewer particles for clarity
        const startX = Math.floor(Math.random() * (canvas.width / gridSize)) * gridSize;
        const startY = Math.floor(Math.random() * (canvas.height / gridSize)) * gridSize;
        const directions = [
            { x: speed, y: 0 }, { x: -speed, y: 0 },
            { x: 0, y: speed }, { x: 0, y: -speed }
        ];
        const newDirection = directions[Math.floor(Math.random() * directions.length)];
        particles.push(new Particle(startX, startY, newDirection.x, newDirection.y));
      }

      // --- New Connection Logic ---
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const p1 = particles[i];
          const p2 = particles[j];
          const dx = p1.x - p2.x;
          const dy = p1.y - p2.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < gridSize * 3) { // Connect if within 3 grid cells
            const opacity = 1 - (distance / (gridSize * 3));
            ctx.strokeStyle = `rgba(54, 169, 247, ${opacity * 0.5})`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.stroke();
          }
        }
      }

      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];
        p.update();
        p.draw();
        if (p.life <= 0 || p.x < 0 || p.x > canvas.width || p.y < 0 || p.y > canvas.height) {
          particles.splice(i, 1);
        }
      }

      animationFrameId = window.requestAnimationFrame(animate);
    };

    setup();
    animate();

    const handleResize = () => {
      setup();
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return <canvas ref={canvasRef} style={{ position: 'absolute', top: 0, left: 0, zIndex: 0 }} />;
};

export default AnimatedGrid; 